import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft, 
  Type as TypeIcon, 
  Image as ImageIcon, 
  Square, 
  Layers, 
  Settings2, 
  Trash2, 
  ZoomIn, 
  ZoomOut,
  UserSquare2,
  Palette,
  MousePointer2,
  Move,
  Circle,
  Triangle,
  Diamond,
  Star,
  Hexagon,
  PenTool
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { stationeryAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Rnd } from 'react-rnd';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

// Constants in MM
const CARD_SIZES = {
  ID_CARD: { width: 54, height: 86, bleed: 3 }, // Vertical standard
  BUSINESS_CARD: { width: 89, height: 51, bleed: 3 } // Horizontal standard
};

const MM_TO_PX = 3.78; // Approximate for screen display (96 DPI)

const PLACEHOLDERS = [
  { label: 'First Name', value: '{firstName}' },
  { label: 'Last Name', value: '{lastName}' },
  { label: 'Employee ID', value: '{employeeCode}' },
  { label: 'Department', value: '{department}' },
  { label: 'Designation', value: '{designation}' },
  { label: 'Phone', value: '{phone}' },
  { label: 'Email', value: '{email}' },
  { label: 'Blood Group', value: '{bloodGroup}' },
  { label: 'Joining Date', value: '{doj}' },
  { label: 'Company Name', value: '{companyName}' },
];

const SHAPE_TYPES = {
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  TRIANGLE: 'triangle',
  DIAMOND: 'diamond',
  STAR: 'star',
  HEXAGON: 'hexagon',
  CUSTOM: 'custom'
};

const SHAPE_ICONS = {
  [SHAPE_TYPES.RECTANGLE]: Square,
  [SHAPE_TYPES.CIRCLE]: Circle,
  [SHAPE_TYPES.TRIANGLE]: Triangle,
  [SHAPE_TYPES.DIAMOND]: Diamond,
  [SHAPE_TYPES.STAR]: Star,
  [SHAPE_TYPES.HEXAGON]: Hexagon,
  [SHAPE_TYPES.CUSTOM]: PenTool
};

const SHAPE_CLIP_PATHS = {
  [SHAPE_TYPES.RECTANGLE]: 'none',
  [SHAPE_TYPES.CIRCLE]: 'circle(50% at 50% 50%)',
  [SHAPE_TYPES.TRIANGLE]: 'polygon(50% 0%, 0% 100%, 100% 100%)',
  [SHAPE_TYPES.DIAMOND]: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
  [SHAPE_TYPES.STAR]: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
  [SHAPE_TYPES.HEXAGON]: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
};

const StationeryDesigner = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const cardType = type?.toUpperCase() || 'ID_CARD';
  const size = CARD_SIZES[cardType] || CARD_SIZES.ID_CARD;
  
  const [templateName, setTemplateName] = useState('New Template');
  const [elements, setElements] = useState({ front: [], back: [] });
  const [side, setSide] = useState('front');
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(1.5);
  const [isDefault, setIsDefault] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('');
  const [backgrounds, setBackgrounds] = useState({ 
    front: '#ffffff', 
    back: '#f8f9fa', 
    frontImage: '', 
    backImage: '' 
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      if (id) {
        const res = await stationeryAPI.getTemplate(id);
        const data = res.data;
        setTemplateName(data.name);
        setElements(data.design_json.elements || { front: [], back: [] });
        setBackgrounds(data.design_json.backgrounds || { front: '#ffffff', back: '#f8f9fa' });
        setIsDefault(data.is_default);
      }
      
      const compRes = await companyAPI.getCurrent();
      if (compRes.data.logo) {
         setCompanyLogo(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}${compRes.data.logo}`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load data');
    }
  };

  const addElement = (elementType, shapeType = SHAPE_TYPES.RECTANGLE) => {
    const newElement = {
      id: Date.now().toString(),
      type: elementType,
      shapeType: elementType === 'shape' ? shapeType : null,
      x: 10,
      y: 10,
      width: elementType === 'text' ? 40 : (elementType === 'shape' ? 30 : 25),
      height: elementType === 'text' ? 5 : (elementType === 'shape' ? 10 : 25),
      content: elementType === 'text' ? 'New Text' : (elementType === 'shape' && shapeType === SHAPE_TYPES.CUSTOM ? 'M 0 0 L 10 0 L 10 10 L 0 10 Z' : ''),
      fontSize: 8,
      fontWeight: 'normal',
      color: elementType === 'shape' ? '#e2e8f0' : '#000000',
      textAlign: 'left',
      opacity: 1,
      zIndex: elements[side].length + 1,
      borderRadius: elementType === 'photo' || (elementType === 'shape' && shapeType === SHAPE_TYPES.CIRCLE) ? 100 : 0
    };
    
    setElements({
      ...elements,
      [side]: [...elements[side], newElement]
    });
    setSelectedId(newElement.id);
  };

  const updateElement = (id, updates) => {
    setElements({
      ...elements,
      [side]: elements[side].map(el => el.id === id ? { ...el, ...updates } : el)
    });
  };

  const deleteElement = (id) => {
    setElements({
      ...elements,
      [side]: elements[side].filter(el => el.id !== id)
    });
    setSelectedId(null);
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: templateName,
        type: cardType,
        design_json: { elements, backgrounds },
        is_default: isDefault
      };
      
      if (id) {
        await stationeryAPI.updateTemplate(id, payload);
        toast.success('Template updated');
      } else {
        await stationeryAPI.createTemplate(payload);
        toast.success('Template created');
        navigate('/stationery');
      }
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const selectedElement = elements[side].find(el => el.id === selectedId);

  const canvasWidth = size.width * MM_TO_PX * zoom;
  const canvasHeight = size.height * MM_TO_PX * zoom;

  return (
    <TooltipProvider>
    <div className="flex flex-col h-screen bg-slate-50 text-slate-900 overflow-hidden">
      {/* Header */}
      <header className="h-16 border-b border-slate-200 flex items-center justify-between px-6 bg-white shrink-0 z-50">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stationery')} className="text-slate-500 hover:bg-slate-100">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex flex-col">
            <input 
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="bg-transparent border-none focus:ring-0 text-lg font-bold p-0 w-64 text-slate-900 placeholder:text-slate-400"
              placeholder="Untitled Template"
            />
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold leading-none">
              Editing {cardType.replace('_', ' ')}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 border border-slate-200">
                <Button 
                   variant="ghost" 
                   size="sm" 
                   className={cn("h-7 text-[10px] px-4 rounded-full transition-all", side === 'front' ? "bg-white text-primary font-bold shadow-sm" : "text-slate-500 hover:text-slate-900")}
                   onClick={() => setSide('front')}
                >
                    FRONT
                </Button>
                <Button 
                   variant="ghost" 
                   size="sm" 
                   className={cn("h-7 text-[10px] px-4 rounded-full transition-all", side === 'back' ? "bg-white text-primary font-bold shadow-sm" : "text-slate-500 hover:text-slate-900")}
                   onClick={() => setSide('back')}
                >
                    BACK
                </Button>
          </div>
          
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-2 border border-slate-200">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" onClick={() => setZoom(Math.max(0.5, zoom - 0.2))}><ZoomOut size={14} /></Button>
            <span className="text-[11px] font-semibold w-10 text-center text-slate-600">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500" onClick={() => setZoom(Math.min(4, zoom + 0.2))}><ZoomIn size={14} /></Button>
          </div>
          
          <Button onClick={handleSave} className="gap-2 shadow-sm font-semibold text-white">
            <Save size={18} /> Save Template
          </Button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Toolbar Left */}
        <aside className="w-16 border-r border-slate-200 bg-white flex flex-col items-center py-6 gap-6 shrink-0 z-40">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-600 hover:bg-slate-100 hover:text-primary transition-colors" onClick={() => addElement('text')}>
                <TypeIcon size={20} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Add Text</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-600 hover:bg-slate-100 hover:text-primary transition-colors" onClick={() => addElement('photo')}>
                <UserSquare2 size={20} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Employee Photo</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-600 hover:bg-slate-100 hover:text-primary transition-colors" onClick={() => addElement('logo')}>
                <ImageIcon size={20} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Company Logo</TooltipContent>
          </Tooltip>
          
          <Popover>
            <PopoverTrigger asChild>
               <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-600 hover:bg-slate-100 hover:text-primary transition-colors">
                  <Square size={20} />
               </Button>
            </PopoverTrigger>
            <PopoverContent side="right" className="w-48 p-2 grid grid-cols-3 gap-2">
               {Object.values(SHAPE_TYPES).map(st => {
                  const Icon = SHAPE_ICONS[st];
                  return (
                     <Button 
                        key={st} 
                        variant="ghost" 
                        size="icon" 
                        className="h-12 w-12 hover:bg-slate-100 hover:text-primary transition-all p-2"
                        onClick={() => addElement('shape', st)}
                     >
                        <Icon size={24} />
                     </Button>
                  );
               })}
            </PopoverContent>
          </Popover>
          
          <div className="mt-auto flex flex-col gap-4">
             <Button variant="ghost" size="icon" className="h-10 w-10 text-slate-400 hover:text-slate-900" onClick={() => setSelectedId(null)}>
               <MousePointer2 size={18} />
             </Button>
          </div>
        </aside>

        {/* Canvas Area */}
        <main className="flex-1 overflow-auto bg-slate-200/50 flex items-center justify-center p-20 relative">
           <div 
             className="relative shadow-2xl transition-all duration-300 pointer-events-auto"
             style={{
               width: canvasWidth,
               height: canvasHeight,
               backgroundColor: backgrounds[side],
               backgroundImage: backgrounds[`${side}Image`] ? `url(${backgrounds[`${side}Image`]})` : 'none',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               color: '#000'
             }}
             onClick={(e) => {
               if (e.target === e.currentTarget) setSelectedId(null);
             }}
           >
             {/* Artboard Border */}
             <div className="absolute inset-0 border border-slate-200 pointer-events-none" />
             
             {/* Bleed Lines (Visual Only) */}
             <div 
               className="absolute inset-0 pointer-events-none border border-red-500/20 border-dashed" 
               style={{ 
                  top: `${size.bleed * MM_TO_PX * zoom}px`,
                  bottom: `${size.bleed * MM_TO_PX * zoom}px`,
                  left: `${size.bleed * MM_TO_PX * zoom}px`,
                  right: `${size.bleed * MM_TO_PX * zoom}px`,
               }} 
             />

             {elements[side].sort((a,b) => a.zIndex - b.zIndex).map((el) => (
               <Rnd
                 key={el.id}
                 size={{ width: el.width * MM_TO_PX * zoom, height: el.height * MM_TO_PX * zoom }}
                 position={{ x: el.x * MM_TO_PX * zoom, y: el.y * MM_TO_PX * zoom }}
                 onDragStop={(e, d) => updateElement(el.id, { x: d.x / (MM_TO_PX * zoom), y: d.y / (MM_TO_PX * zoom) })}
                 onResizeStop={(e, direction, ref, delta, position) => {
                   updateElement(el.id, {
                     width: ref.offsetWidth / (MM_TO_PX * zoom),
                     height: ref.offsetHeight / (MM_TO_PX * zoom),
                     x: position.x / (MM_TO_PX * zoom),
                     y: position.y / (MM_TO_PX * zoom)
                   });
                 }}
                 bounds="parent"
                 onClick={(e) => {
                   e.stopPropagation();
                   setSelectedId(el.id);
                 }}
                 className={cn(
                   "group cursor-move transition-shadow",
                   selectedId === el.id ? "ring-2 ring-primary z-[100]" : "hover:ring-1 hover:ring-primary/40"
                 )}
                 style={{ zIndex: el.zIndex }}
               >
                 <div 
                   className="w-full h-full overflow-hidden flex items-center relative" 
                   style={{ 
                     justifyContent: el.textAlign === 'center' ? 'center' : (el.textAlign === 'right' ? 'flex-end' : 'flex-start'),
                     borderRadius: (el.type === 'shape' && el.shapeType === SHAPE_TYPES.CIRCLE) ? '100%' : (el.borderRadius ? `${el.borderRadius * zoom}px` : 0),
                     backgroundColor: (el.type === 'shape' && el.shapeType !== SHAPE_TYPES.CUSTOM) ? el.color : 'transparent',
                     clipPath: el.type === 'shape' ? SHAPE_CLIP_PATHS[el.shapeType || SHAPE_TYPES.RECTANGLE] : 'none',
                     opacity: el.opacity
                   }}
                 >
                    {el.type === 'text' && (
                      <div style={{ 
                        fontSize: `${el.fontSize * zoom}px`, 
                        fontWeight: el.fontWeight, 
                        color: el.color,
                        textAlign: el.textAlign,
                        lineHeight: 1.1,
                        width: '100%',
                        padding: '2px'
                      }}>
                        {el.content}
                      </div>
                    )}
                    {el.type === 'photo' && (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center border border-dashed border-slate-300">
                        <UserSquare2 size={el.width * MM_TO_PX * zoom * 0.6} className="text-slate-300" />
                        <div className="absolute inset-x-0 bottom-0 bg-slate-800/80 text-[6px] text-white text-center py-0.5 tracking-tighter uppercase font-bold">Photo Placeholder</div>
                      </div>
                    )}
                    {el.type === 'logo' && (
                      <div className="w-full h-full flex items-center justify-center bg-slate-50/50">
                        {companyLogo ? (
                          <img src={companyLogo} alt="Logo" className="max-w-[90%] max-h-[90%] object-contain" />
                        ) : (
                          <ImageIcon className="text-slate-300" size={el.width * MM_TO_PX * zoom * 0.5} />
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-slate-800/80 text-[6px] text-white text-center py-0.5 uppercase font-bold">Logo</div>
                      </div>
                    )}
                    {el.type === 'shape' && el.shapeType === SHAPE_TYPES.CUSTOM && (
                       <svg width="100%" height="100%" viewBox="0 0 10 10" preserveAspectRatio="none">
                          <path d={el.content || 'M 0 0 L 10 0 L 10 10 L 0 10 Z'} fill={el.color} />
                       </svg>
                    )}
                 </div>
               </Rnd>
             ))}
           </div>
        </main>

        {/* Properties Panel Right */}
        <aside className="w-80 border-l border-slate-200 bg-white flex flex-col shrink-0 z-40 shadow-xl">
          <Tabs defaultValue="properties" className="flex-1 flex flex-col">
            <div className="px-4 py-2 border-b border-slate-100">
              <TabsList className="bg-slate-50 w-full p-1 h-11 gap-1 border border-slate-200">
                <TabsTrigger value="properties" className="flex-1 text-[10px] uppercase font-bold tracking-wider data-[state=active]:bg-white data-[state=active]:shadow-sm">Properties</TabsTrigger>
                <TabsTrigger value="canvas" className="flex-1 text-[10px] uppercase font-bold tracking-wider data-[state=active]:bg-white data-[state=active]:shadow-sm">Canvas</TabsTrigger>
              </TabsList>
            </div>
          
            <TabsContent value="properties" className="p-5 flex-1 overflow-auto space-y-6">
              {!selectedElement ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <MousePointer2 className="text-slate-400" size={20} />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1">No Element Selected</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">Click on an element on the canvas to edit its properties.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-primary/10 rounded-md">
                        <Settings2 size={14} className="text-primary" />
                      </div>
                      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-900">Edit {selectedElement.type}</h3>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-destructive hover:bg-destructive/5" onClick={() => deleteElement(selectedId)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  
                  {selectedElement.type === 'text' && (
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold text-slate-500 uppercase">Text Content</Label>
                        <Input 
                          value={selectedElement.content} 
                          onChange={(e) => updateElement(selectedId, { content: e.target.value })}
                          className="bg-slate-50 border-slate-200 h-10 text-sm focus:bg-white"
                        />
                      </div>
                      
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold text-slate-500 uppercase">Dynamic Placeholders</Label>
                        <Select onValueChange={(val) => updateElement(selectedId, { content: selectedElement.content + val })}>
                          <SelectTrigger className="h-10 text-xs bg-slate-50 border-slate-200">
                            <SelectValue placeholder="Insert dynamic data..." />
                          </SelectTrigger>
                          <SelectContent>
                            {PLACEHOLDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Size (pt)</Label>
                          <Input 
                            type="number"
                            value={selectedElement.fontSize} 
                            onChange={(e) => updateElement(selectedId, { fontSize: parseFloat(e.target.value) })}
                            className="bg-slate-50 border-slate-200 h-10 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Weight</Label>
                          <Select value={selectedElement.fontWeight} onValueChange={(val) => updateElement(selectedId, { fontWeight: val })}>
                            <SelectTrigger className="h-10 text-xs bg-slate-50 border-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="600">Semi Bold</SelectItem>
                              <SelectItem value="bold">Bold</SelectItem>
                              <SelectItem value="900">Black</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                         <Label className="text-[10px] font-bold text-slate-500 uppercase">Text Color</Label>
                         <div className="flex gap-2">
                            <input 
                              type="color" 
                              value={selectedElement.color} 
                              onChange={(e) => updateElement(selectedId, { color: e.target.value })}
                              className="w-10 h-10 rounded border border-slate-200 bg-white cursor-pointer p-1"
                            />
                            <Input 
                              value={selectedElement.color} 
                              onChange={(e) => updateElement(selectedId, { color: e.target.value })}
                              className="bg-slate-50 border-slate-200 h-10 text-xs font-mono"
                            />
                         </div>
                      </div>

                      <div className="space-y-1.5">
                         <Label className="text-[10px] font-bold text-slate-500 uppercase">Alignment</Label>
                         <div className="flex gap-1 p-1 bg-slate-100 rounded-lg border border-slate-200">
                            {['left', 'center', 'right'].map(align => (
                              <Button 
                                key={align} 
                                variant="ghost" 
                                size="sm" 
                                className={cn("flex-1 h-8 text-[9px] uppercase font-bold transition-all", selectedElement.textAlign === align ? "bg-white text-primary shadow-sm" : "text-slate-500 hover:text-slate-900")}
                                onClick={() => updateElement(selectedId, { textAlign: align })}
                              >
                                 {align}
                              </Button>
                            ))}
                         </div>
                      </div>
                    </div>
                  )}

                  {selectedElement.type === 'shape' && (
                    <div className="space-y-4">
                       <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Shape Type</Label>
                          <Select value={selectedElement.shapeType} onValueChange={(val) => updateElement(selectedId, { shapeType: val, borderRadius: val === SHAPE_TYPES.CIRCLE ? 100 : 0 })}>
                            <SelectTrigger className="h-10 text-xs bg-slate-50 border-slate-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(SHAPE_TYPES).map(([key, val]) => (
                                <SelectItem key={val} value={val}>{key.charAt(0) + key.slice(1).toLowerCase()}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                       </div>

                       <div className="space-y-1.5">
                         <Label className="text-[10px] font-bold text-slate-500 uppercase">Fill Color</Label>
                         <div className="flex gap-2">
                            <input 
                               type="color" 
                               value={selectedElement.color} 
                               onChange={(e) => updateElement(selectedId, { color: e.target.value })}
                               className="w-10 h-10 rounded border border-slate-200 bg-white cursor-pointer p-1"
                            />
                            <Input 
                               value={selectedElement.color} 
                               onChange={(e) => updateElement(selectedId, { color: e.target.value })}
                               className="bg-slate-50 border-slate-200 h-10 text-xs font-mono"
                            />
                         </div>
                      </div>
                      
                      {selectedElement.shapeType === SHAPE_TYPES.CUSTOM ? (
                        <div className="space-y-1.5">
                           <Label className="text-[10px] font-bold text-slate-500 uppercase">SVG Path Data</Label>
                           <Input 
                              value={selectedElement.content} 
                              onChange={(e) => updateElement(selectedId, { content: e.target.value })}
                              placeholder="M 0 0 L 10 0..."
                              className="bg-slate-50 border-slate-200 h-10 text-xs font-mono"
                           />
                           <p className="text-[9px] text-slate-400">Relative to 0-10 units viewport.</p>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                            <Label className="text-[10px] font-bold text-slate-500 uppercase">Corner Radius (mm)</Label>
                            <Input 
                              type="number"
                              value={selectedElement.borderRadius || 0} 
                              onChange={(e) => updateElement(selectedId, { borderRadius: parseFloat(e.target.value) })}
                              className="bg-slate-50 border-slate-200 h-10 text-sm"
                            />
                          </div>
                      )}
                    </div>
                  )}
                  
                  <div className="pt-6 border-t border-slate-100 space-y-4">
                     <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Width (mm)</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={Math.round(selectedElement.width * 10) / 10} 
                            onChange={(e) => updateElement(selectedId, { width: parseFloat(e.target.value) })}
                            className="bg-slate-50 border-slate-200 h-10 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Height (mm)</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={Math.round(selectedElement.height * 10) / 10} 
                            onChange={(e) => updateElement(selectedId, { height: parseFloat(e.target.value) })}
                            className="bg-slate-50 border-slate-200 h-10 text-sm"
                          />
                        </div>
                     </div>
                     <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">X-Axis (mm)</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={Math.round(selectedElement.x * 10) / 10} 
                            onChange={(e) => updateElement(selectedId, { x: parseFloat(e.target.value) })}
                            className="bg-slate-50 border-slate-200 h-10 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-bold text-slate-500 uppercase">Y-Axis (mm)</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={Math.round(selectedElement.y * 10) / 10} 
                            onChange={(e) => updateElement(selectedId, { y: parseFloat(e.target.value) })}
                            className="bg-slate-50 border-slate-200 h-10 text-sm"
                          />
                        </div>
                     </div>
                  </div>
                </>
              )}
            </TabsContent>
          
            <TabsContent value="canvas" className="p-5 flex-1 overflow-auto space-y-6">
               <div className="space-y-1.5">
                  <Label className="text-[10px] font-black uppercase text-slate-500">Board Background</Label>
                  <div className="flex gap-2">
                    <input 
                      type="color" 
                      value={backgrounds[side]} 
                      onChange={(e) => setBackgrounds({ ...backgrounds, [side]: e.target.value })}
                      className="w-12 h-12 rounded border border-slate-200 bg-white cursor-pointer p-1 shadow-sm"
                    />
                    <Input 
                      value={backgrounds[side]} 
                      onChange={(e) => setBackgrounds({ ...backgrounds, [side]: e.target.value })}
                      className="bg-slate-50 border-slate-200 h-12 text-sm font-mono"
                    />
                  </div>
               </div>
               
               <div className="space-y-1.5">
                  <Label className="text-[10px] font-black uppercase text-slate-500">Image Asset Layer</Label>
                  <Input 
                    value={backgrounds[`${side}Image`] || ''} 
                    onChange={(e) => setBackgrounds({ ...backgrounds, [`${side}Image`]: e.target.value })}
                    placeholder="Enter image/SVG URL..."
                    className="bg-slate-50 border-slate-200 h-10 text-xs"
                  />
                  <p className="text-[9px] text-slate-400 italic mt-1 font-medium">Resolution: {size.width} x {size.height} mm (300 DPI recommended)</p>
               </div>
               
               <div className="pt-6 border-t border-slate-100 space-y-5">
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-200">
                    <div>
                      <Label className="text-xs font-bold text-slate-900">Set as Primary</Label>
                      <p className="text-[10px] text-slate-500">Default for all ID generation</p>
                    </div>
                    <Switch checked={isDefault} onCheckedChange={setIsDefault} />
                  </div>
                  
                  <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex items-start gap-3">
                     <Palette className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                     <p className="text-[10px] text-blue-800 leading-relaxed font-medium">
                        Pro Tip: Use <span className="font-bold">Transparent PNGs/SVGs</span> for overlays to create depth and professional branding.
                     </p>
                  </div>
               </div>
            </TabsContent>
          </Tabs>
          
          <div className="p-4 bg-slate-50 border-t border-slate-200">
             <div className="flex items-center justify-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em]">Teamly Design Studio v1.0</span>
             </div>
             <p className="text-[8px] text-center text-slate-500 font-mono tracking-tighter uppercase font-bold">Vector Graphics Engine • Millimeter Precision</p>
          </div>
        </aside>
      </div>
    </div>
    </TooltipProvider>
  );
};

export default StationeryDesigner;
